import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
    ArrowLeft,
    KeyRound,
    Shield,
    Save,
    Users,
    LayoutDashboard,
    Calendar,
    BookOpen,
    Clock,
    ChevronDown,
    ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '../components/ui/collapsible';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// All roles in the system
const ALL_ROLES = [
    { key: 'network_administrator', label: 'Network Administrator', level: 0 },
    { key: 'network_manager', label: 'Network Manager', level: 0 },
    { key: 'local_controller', label: 'Local Controller', level: 1 },
    { key: 'deputy_local_controller', label: 'Deputy Local Controller', level: 1 },
    { key: 'unit_duty_officer', label: 'Unit Duty Officer', level: 1 },
    { key: 'group_administrator', label: 'Group Administrator', level: 2 },
    { key: 'group_leader', label: 'Group Leader', level: 2 },
    { key: 'deputy_group_leader', label: 'Deputy Group Leader', level: 2 },
    { key: 'general_member', label: 'General Member', level: 3 },
];

// Permission categories - only include pages that exist
const PERMISSION_CATEGORIES = [
    {
        id: 'portal_sections',
        label: 'Portal Sections',
        description: 'Control which roles can access sections on the main portal',
        icon: LayoutDashboard,
        permissions: [
            { key: 'portal_calendar', label: 'Calendar', icon: Calendar },
            { key: 'portal_logbook', label: 'Logbook', icon: BookOpen },
            { key: 'portal_members', label: 'Members Directory', icon: Users },
        ]
    },
    {
        id: 'attendance_features',
        label: 'Attendance Features',
        description: 'Control attendance-related capabilities',
        icon: Clock,
        permissions: [
            { key: 'duty_officer_clockon', label: 'Duty Officer Clock-on Button', icon: Shield },
            { key: 'view_active_members', label: 'View Active Members', icon: Users },
        ]
    },
    {
        id: 'dashboard_sections',
        label: 'Dashboard Sections',
        description: 'Control what roles can see on the Admin Dashboard',
        icon: LayoutDashboard,
        permissions: [
            { key: 'dashboard_metrics', label: 'View Dashboard Metrics', icon: LayoutDashboard },
            { key: 'dashboard_recent_activity', label: 'View Recent Activity', icon: Clock },
            { key: 'manage_members', label: 'Access Member Management', icon: Users },
            { key: 'manage_calendar', label: 'Access Calendar Management', icon: Calendar },
            { key: 'manage_activities', label: 'Access Activity Type Management', icon: Clock },
            { key: 'access_timecard_page', label: 'Access Timecard Page', icon: Clock },
        ]
    },
];

// Default permissions by role
const getDefaultPermissions = () => {
    const defaults = {};
    
    ALL_ROLES.forEach(role => {
        defaults[role.key] = {};
        
        PERMISSION_CATEGORIES.forEach(category => {
            category.permissions.forEach(permission => {
                // Network/Unit level roles get everything by default
                if (role.level <= 1) {
                    defaults[role.key][permission.key] = true;
                }
                // Group level roles get portal sections and limited dashboard
                else if (role.level === 2) {
                    if (category.id === 'portal_sections') {
                        defaults[role.key][permission.key] = true;
                    } else if (permission.key === 'duty_officer_clockon') {
                        defaults[role.key][permission.key] = false;
                    } else if (permission.key === 'dashboard_metrics' || permission.key === 'dashboard_availability') {
                        defaults[role.key][permission.key] = true;
                    } else {
                        defaults[role.key][permission.key] = false;
                    }
                }
                // General members get portal sections only
                else {
                    if (category.id === 'portal_sections') {
                        defaults[role.key][permission.key] = true;
                    } else {
                        defaults[role.key][permission.key] = false;
                    }
                }
            });
        });
    });
    
    return defaults;
};

export default function PermissionEditorPage() {
    const navigate = useNavigate();
    const { isNetworkManager } = useAuth();
    const [permissions, setPermissions] = useState(getDefaultPermissions());
    const [expandedRoles, setExpandedRoles] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        fetchPermissions();
    }, []);

    const fetchPermissions = async () => {
        try {
            const response = await axios.get(`${API_URL}/admin/role-permissions`);
            if (response.data.permissions && Object.keys(response.data.permissions).length > 0) {
                // Merge fetched permissions with defaults
                const merged = { ...getDefaultPermissions() };
                Object.keys(response.data.permissions).forEach(role => {
                    if (merged[role]) {
                        merged[role] = { ...merged[role], ...response.data.permissions[role] };
                    }
                });
                setPermissions(merged);
            }
        } catch (error) {
            console.log('Using default permissions');
        } finally {
            setLoading(false);
        }
    };

    const handlePermissionChange = (role, permission, value) => {
        setPermissions(prev => ({
            ...prev,
            [role]: {
                ...prev[role],
                [permission]: value
            }
        }));
        setHasChanges(true);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.post(`${API_URL}/admin/role-permissions`, { permissions });
            toast.success('Permissions saved successfully');
            setHasChanges(false);
        } catch (error) {
            toast.error('Failed to save permissions');
        } finally {
            setSaving(false);
        }
    };

    const toggleRole = (roleKey) => {
        setExpandedRoles(prev => ({
            ...prev,
            [roleKey]: !prev[roleKey]
        }));
    };

    const getRolePermissionCount = (roleKey) => {
        const rolePerms = permissions[roleKey] || {};
        // Only count permissions that are defined in PERMISSION_CATEGORIES
        const definedPermissionKeys = PERMISSION_CATEGORIES.flatMap(cat => 
            cat.permissions.map(p => p.key)
        );
        const enabled = definedPermissionKeys.filter(key => rolePerms[key] === true).length;
        const total = definedPermissionKeys.length;
        return { enabled, total };
    };

    if (!isNetworkManager()) {
        return (
            <div className="flex items-center justify-center p-4">
                <Card className="max-w-md w-full">
                    <CardContent className="p-6 text-center">
                        <KeyRound className="w-12 h-12 mx-auto mb-4 text-destructive" />
                        <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
                        <p className="text-muted-foreground mb-4">
                            Only Network Managers can access the Permission Editor.
                        </p>
                        <Button onClick={() => navigate('/dashboard')}>
                            Return to Dashboard
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="permission-editor-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold flex items-center gap-2">
                        <KeyRound className="w-7 h-7 text-primary" />
                        Permission Editor
                    </h2>
                    <p className="text-muted-foreground mt-1">Configure role-based access controls</p>
                </div>
                <Button
                    onClick={handleSave}
                    disabled={!hasChanges || saving}
                    data-testid="save-permissions-btn"
                >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                </Button>
            </div>

            {/* Info Card */}
            <Card className="bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
                <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                        <KeyRound className="w-5 h-5 text-blue-600 mt-0.5" />
                        <div>
                            <p className="font-medium text-blue-900 dark:text-blue-100">
                                Role Permission Management
                            </p>
                            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                                Configure what each role can see and access throughout the system. 
                                Changes apply to all users with that role.
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {loading ? (
                    <div className="space-y-4">
                        {[...Array(3)].map((_, i) => (
                            <div key={i} className="h-20 bg-muted rounded-lg animate-pulse"></div>
                        ))}
                    </div>
                ) : (
                    /* Role Permission Cards */
                    <div className="space-y-3">
                        {ALL_ROLES.map((role) => {
                            const isExpanded = expandedRoles[role.key];
                            const { enabled, total } = getRolePermissionCount(role.key);
                            
                            return (
                                <Card key={role.key}>
                                    <Collapsible open={isExpanded} onOpenChange={() => toggleRole(role.key)}>
                                        <CollapsibleTrigger asChild>
                                            <button
                                                className="w-full p-4 flex items-center justify-between hover:bg-muted/30 transition-colors"
                                                data-testid={`role-${role.key}`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <Shield className="w-5 h-5 text-primary" />
                                                    <div className="text-left">
                                                        <p className="font-medium">{role.label}</p>
                                                        <p className="text-sm text-muted-foreground">
                                                            Level {role.level} • {enabled}/{total} permissions enabled
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Badge variant="secondary">
                                                        {enabled}/{total}
                                                    </Badge>
                                                    {isExpanded ? (
                                                        <ChevronDown className="w-5 h-5 text-muted-foreground" />
                                                    ) : (
                                                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                                                    )}
                                                </div>
                                            </button>
                                        </CollapsibleTrigger>
                                        
                                        <CollapsibleContent>
                                            <div className="border-t">
                                                {PERMISSION_CATEGORIES.map((category) => (
                                                    <div key={category.id} className="p-4 border-b last:border-b-0">
                                                        <div className="flex items-center gap-2 mb-3">
                                                            <category.icon className="w-4 h-4 text-muted-foreground" />
                                                            <h4 className="font-medium text-sm">{category.label}</h4>
                                                        </div>
                                                        <p className="text-xs text-muted-foreground mb-3">
                                                            {category.description}
                                                        </p>
                                                        <div className="space-y-2">
                                                            {category.permissions.map((permission) => (
                                                                <div
                                                                    key={permission.key}
                                                                    className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30"
                                                                >
                                                                    <div className="flex items-center gap-2">
                                                                        <permission.icon className="w-4 h-4 text-muted-foreground" />
                                                                        <span className="text-sm">{permission.label}</span>
                                                                    </div>
                                                                    <Switch
                                                                        checked={permissions[role.key]?.[permission.key] ?? false}
                                                                        onCheckedChange={(checked) => 
                                                                            handlePermissionChange(role.key, permission.key, checked)
                                                                        }
                                                                        data-testid={`perm-${role.key}-${permission.key}`}
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </CollapsibleContent>
                                    </Collapsible>
                                </Card>
                            );
                        })}
                    </div>
                )}

            {hasChanges && (
                <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-auto z-50">
                    <Card className="bg-primary text-white">
                        <CardContent className="p-3 flex items-center justify-between gap-4">
                            <span className="text-sm">You have unsaved changes</span>
                            <Button
                                size="sm"
                                variant="secondary"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                <Save className="w-4 h-4 mr-1" />
                                Save
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
}
